import get from 'lodash/get';
import formatMetricValue from 'app/util/formatMetricValue';
import { THRESHOLD_CONDITION_TYPES, COMPARISON_DIRECTIONS } from '@kentik/ui-shared/alerting/constants';

export function getRawCapacity(alert) {
  const { dimensionToKeyDetail } = alert;

  return (
    (get(dimensionToKeyDetail, 'InterfaceID_src.interface.snmpSpeedMbps') ||
      get(dimensionToKeyDetail, 'InterfaceID_dst.interface.snmpSpeedMbps') ||
      get(dimensionToKeyDetail, 'ktappprotocol__snmp__output_port.interface.snmpSpeedMbps') ||
      0) * 1000000
  );
}

export function getRatioConditionValues(ratioSettings, metrics, metricToValue) {
  const {
    lhsMetricPosition = 0, // These default 0s are required to solve api sometimes not returning 0 values
    rhsMetricPosition = 0,
    lhsMetricProportion,
    rhsMetricProportion,
    margin = 0
  } = ratioSettings;
  const lhMetric = metrics[lhsMetricPosition];
  const rhMetric = metrics[rhsMetricPosition];
  const lhValue = metricToValue[lhMetric];
  const rhValue = metricToValue[rhMetric];
  const lhFormatted = formatMetricValue(lhValue, lhMetric);
  const rhFormatted = formatMetricValue(rhValue, rhMetric);
  const alertCondition = `${lhsMetricProportion + margin}:${rhsMetricProportion}`;
  // the reverse calculation determines whether this is a bi-directional condition that triggered and
  // left right values need to be reversed to make sense. It does so by comparing the ratio of the
  // actual metric values to the lh and rh side proportions ratio.
  const reverse = lhValue / rhValue < (lhsMetricProportion + margin) / rhsMetricProportion;
  const actualValue = `${lhFormatted.formatted} : ${rhFormatted.formatted}`;
  const reverseValue = `${rhFormatted.formatted} : ${lhFormatted.formatted}`;

  return {
    lhMetric: reverse ? rhMetric : lhMetric,
    rhMetric: reverse ? lhMetric : rhMetric,
    alertCondition,
    actualValue: reverse ? reverseValue : actualValue
  };
}

export function getThresholdValueForConditionType(condition, threshold, alertModel) {
  const { primaryMetric, sortedMetrics: metrics, metricToValue, baselineValue: baseline } = alertModel;
  const { direction } = threshold;
  const {
    type,
    comparisonValue = 0,
    comparisonValueFactor = 1,
    metric = primaryMetric,
    fallbackSettings = {},
    ratioSettings
  } = condition || {};
  const currentValue = metricToValue[metric];
  const baselineValue = baseline || Number(fallbackSettings?.value) || 0;
  const capacity = getRawCapacity(alertModel, threshold);

  const useCurrentValue = direction === COMPARISON_DIRECTIONS.TO_HISTORY;
  const isPrimaryMetric = metric === primaryMetric;
  const metricValue = useCurrentValue ? currentValue : baselineValue;

  // For the following conditions, the baseline value is in the primary metric.
  // If we're comparing baseline to current, the metric must be the primary one.

  if (isPrimaryMetric || useCurrentValue) {
    if (type === THRESHOLD_CONDITION_TYPES.STATIC) {
      return {
        metricValue,
        thresholdValue: comparisonValue * comparisonValueFactor
      };
    }

    if (type === THRESHOLD_CONDITION_TYPES.CAPACITY) {
      return {
        metricValue,
        thresholdValue: capacity - comparisonValue * 1000000
      };
    }

    if (type === THRESHOLD_CONDITION_TYPES.CAPACITY_PERCENT) {
      return {
        metricValue,
        thresholdValue: (capacity * comparisonValue) / 100
      };
    }
  }

  // Leaving commented since we don't return a value for this condition type
  // if (type === THRESHOLD_CONDITION_TYPES.TOP_KEYS) { }

  // For baseline conditions, we must have the primary metric
  if (isPrimaryMetric) {
    if (type === THRESHOLD_CONDITION_TYPES.BASELINE) {
      return {
        metricValue,
        thresholdValue: (useCurrentValue ? baselineValue : currentValue) + comparisonValue
      };
    }

    if (type === THRESHOLD_CONDITION_TYPES.BASELINE_PERCENT) {
      return {
        metricValue,
        thresholdValue: ((useCurrentValue ? baselineValue : currentValue) * (comparisonValue + 100)) / 100
      };
    }
  }

  if (type === THRESHOLD_CONDITION_TYPES.RATIO) {
    return { ratio: getRatioConditionValues(ratioSettings, metrics, metricToValue) };
  }

  return null;
}
